import { create } from "zustand";
import type { AllocationRow, BalanceSnapshot } from "@capture/shared";
import type { RemittanceStationPayload, StationTransaction } from "../api/client";
import { deriveTransactionLive, type LiveDerivation } from "../lib/live-balance";

interface StationState {
  payload: RemittanceStationPayload | null;
  activeTransactionId: string | null;
  /** docId → current keyed amount in cents (null = present but unparseable). */
  amountOverrides: Record<string, number | null>;
  /** txnId → in-progress allocation rows (before PUT). */
  allocationDraft: Record<string, AllocationRow[]>;

  loadPayload: (payload: RemittanceStationPayload) => void;
  setActiveTransaction: (txnId: string) => void;
  setDocAmount: (docId: string, cents: number | null) => void;
  setAllocationDraft: (txnId: string, rows: AllocationRow[]) => void;
  /** Replace a single transaction (after accept/repair/override refetch of one). */
  patchTransaction: (txnId: string, partial: Partial<StationTransaction>) => void;
  /** Remove a transaction from the rail (accepted → advanced out of the step). */
  removeTransaction: (txnId: string) => void;
  /** Imperative use ONLY (event handlers, tests). For render-time reads use
   *  `useLiveBalance(txnId)` — these getters are stable and do NOT subscribe to
   *  the state they read, so calling them in render freezes on keying (F-013). */
  liveBalance: (txnId: string) => BalanceSnapshot | null;
  /** SP7 §7 — the full live derivation (snapshot + policy outcome + the
   *  policy-final allocation set), one call, two views (see liveBalance).
   *  Imperative use ONLY — render-time reads go through `useLiveDerivation`. */
  livePolicyOutcome: (txnId: string) => LiveDerivation | null;
}

export const useStationStore = create<StationState>((set, get) => ({
  payload: null,
  activeTransactionId: null,
  amountOverrides: {},
  allocationDraft: {},

  loadPayload: (payload) =>
    set(() => ({
      payload,
      activeTransactionId: payload.transactions[0]?.id ?? null,
      amountOverrides: {},
      allocationDraft: Object.fromEntries(payload.transactions.filter((t) => t.allocations).map((t) => [t.id, t.allocations!])),
    })),

  setActiveTransaction: (txnId) => set({ activeTransactionId: txnId }),

  setDocAmount: (docId, cents) =>
    set((s) => ({ amountOverrides: { ...s.amountOverrides, [docId]: cents } })),

  setAllocationDraft: (txnId, rows) =>
    set((s) => ({ allocationDraft: { ...s.allocationDraft, [txnId]: rows } })),

  patchTransaction: (txnId, partial) =>
    set((s) => {
      if (!s.payload) return {};
      return {
        payload: {
          ...s.payload,
          transactions: s.payload.transactions.map((t) => (t.id === txnId ? { ...t, ...partial } : t)),
        },
      };
    }),

  removeTransaction: (txnId) =>
    set((s) => {
      if (!s.payload) return {};
      const remaining = s.payload.transactions.filter((t) => t.id !== txnId);
      const active = s.activeTransactionId === txnId ? remaining[0]?.id ?? null : s.activeTransactionId;
      return { payload: { ...s.payload, transactions: remaining }, activeTransactionId: active };
    }),

  livePolicyOutcome: (txnId) => {
    const { payload, amountOverrides, allocationDraft } = get();
    const txn = payload?.transactions.find((t) => t.id === txnId);
    if (!txn || !payload) return null;
    const rows = allocationDraft[txnId] ?? txn.allocations ?? [];
    return deriveTransactionLive(txn, amountOverrides, payload.config, rows);
  },

  liveBalance: (txnId) => get().livePolicyOutcome(txnId)?.snapshot ?? null,
}));
